import type * as React from 'react'
import { Link, NavLink } from 'react-router-dom'

import { NavUser } from '@/components/nav-user'
import { RailSettings } from '@/components/rail-settings'
import { Shimmer, shimmerHost } from '@/components/shimmer'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { NAV_SECTIONS, type NavItem, type SectionId } from '@/lib/nav'
import { prefetchProps } from '@/lib/route-chunks'

/**
 * The inner menu: the selected section's destinations, nothing else. On a
 * /settings page that is Settings' own pages; the door INTO Settings is the
 * gear at the foot of the RAIL, above the account avatar.
 *
 * On a phone there is no rail, so the sheet shows every section at once —
 * a collapsed rail on a screen with no room for it would strand two thirds
 * of the app — and the rail's foot (Settings, account) moves into the footer.
 */
export function AppSidebar({
  section,
  ...props
}: { section: SectionId } & React.ComponentProps<typeof Sidebar>) {
  const { isMobile } = useSidebar()

  const sections = isMobile ? NAV_SECTIONS : NAV_SECTIONS.filter((s) => s.id === section)

  return (
    <Sidebar collapsible="offcanvas" className="md:left-[var(--rail-width,3.5rem)]" {...props}>
      <SidebarHeader>
        <Link
          to="/"
          {...prefetchProps('/')}
          {...shimmerHost}
          aria-label="Warike home"
          className="flex w-fit items-center p-1.5 text-foreground"
        >
          <Shimmer variant="text" className="text-base font-semibold">
            Warike
          </Shimmer>
        </Link>
      </SidebarHeader>
      <SidebarContent>
        {sections.map((s) => (
          <SidebarGroup key={s.id}>
            <SidebarGroupLabel>{s.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              {s.items.length === 0 ? (
                <p className="px-2 py-1.5 text-xs text-muted-foreground">Nothing here yet.</p>
              ) : (
                <SidebarMenu>
                  {s.items.map((item) => (
                    <MenuItem key={item.title} item={item} />
                  ))}
                </SidebarMenu>
              )}
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      {isMobile && (
        <SidebarFooter>
          <div className="flex items-center gap-2 px-1">
            <RailSettings side="top" />
            <NavUser side="top" align="start" />
          </div>
        </SidebarFooter>
      )}
    </Sidebar>
  )
}

function MenuItem({ item }: { item: NavItem }) {
  if (item.children) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton className="pointer-events-none">
          <item.icon />
          <span>{item.title}</span>
        </SidebarMenuButton>
        <SidebarMenuSub>
          {item.children.map((child) => (
            <SidebarMenuSubItem key={child.url}>
              <NavLink to={child.url} {...prefetchProps(child.url)}>
                {({ isActive }) => (
                  <SidebarMenuSubButton asChild isActive={isActive}>
                    <span>
                      <child.icon />
                      <span>{child.title}</span>
                    </span>
                  </SidebarMenuSubButton>
                )}
              </NavLink>
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      </SidebarMenuItem>
    )
  }

  if (!item.url) return null

  const url = item.url
  return (
    <SidebarMenuItem>
      <NavLink to={url} end={item.end} {...prefetchProps(url)}>
        {({ isActive }) => (
          <SidebarMenuButton tooltip={item.title} isActive={isActive}>
            <item.icon />
            <span>{item.title}</span>
          </SidebarMenuButton>
        )}
      </NavLink>
    </SidebarMenuItem>
  )
}
